import React, { useState, useEffect } from 'react';
import './App.css';
import AuthPage      from './pages/AuthPage';
import LoginPage     from './pages/LoginPage';
import AnalyzerPage  from './pages/AnalyzerPage';
import KiteDashboard from './pages/KiteDashboard';
import logo from './resources/BSG_Traders.png';
import { exchangeKiteToken } from './services/apiService';

const SESSION_KEY      = 'bsg_user_session';
const KITE_SESSION_KEY = 'kite_session';

function App() {
  const [user, setUser] = useState(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const [config,      setConfig]      = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Kite session — stored in sessionStorage so it survives a page refresh
  // but is cleared when the tab closes (same policy as the BSG session).
  const [kiteSession, setKiteSession] = useState(() => {
    try {
      const saved = sessionStorage.getItem(KITE_SESSION_KEY);
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      // Discard sessions stored in old snake_case format
      if (parsed.access_token && !parsed.accessToken) {
        sessionStorage.removeItem(KITE_SESSION_KEY);
        return null;
      }
      // Discard if stored before today 6:00 AM IST — token is invalid
      const storedAt = parsed._storedAt;
      if (storedAt) {
        const now = new Date();
        const expiry6am = new Date();
        expiry6am.setUTCHours(0, 30, 0, 0); // 6:00 AM IST = 00:30 UTC
        if (new Date(storedAt) < expiry6am && now >= expiry6am) {
          sessionStorage.removeItem(KITE_SESSION_KEY);
          return null;
        }
      }
      return parsed;
    } catch { return null; }
  });
  const [kiteLoading, setKiteLoading] = useState(false);
  const [kiteError,   setKiteError]   = useState(null);

  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = logo;
  }, []);

  // After Kite login, Zerodha redirects back here with ?request_token=&status=success.
  // Only process the callback for ADMIN users — non-admins never see Kite.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestToken = params.get('request_token');
    const status       = params.get('status');

    if (!requestToken || status !== 'success') return;

    // Always clear the query params from the URL
    window.history.replaceState({}, '', window.location.pathname);

    const currentUser = (() => {
      try { return JSON.parse(sessionStorage.getItem(SESSION_KEY)); } catch { return null; }
    })();

    if (currentUser?.role !== 'ADMIN') return;

    setKiteLoading(true);
    setKiteError(null);
    exchangeKiteToken(requestToken, currentUser.username)
      .then(session => {
        const stamped = { ...session, _storedAt: new Date().toISOString() };
        sessionStorage.setItem(KITE_SESSION_KEY, JSON.stringify(stamped));
        setKiteSession(stamped);
      })
      .catch(err => setKiteError(err.message || 'Kite login failed'))
      .finally(() => setKiteLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAuth = (authenticatedUser) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(authenticatedUser));
    setUser(authenticatedUser);
  };

  // Reflects a self-service profile change (e.g. risk/lot settings) immediately without
  // requiring re-login, and persists it so a page refresh doesn't revert to stale data.
  const handleUpdateUser = (updatedUser) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const handleLogin = (configData) => {
    setConfig(configData);
    setIsAnalyzing(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setIsAnalyzing(false);
    setConfig(null);
    setUser(null);
  };

  const handleBackToLogin = () => {
    setIsAnalyzing(false);
    setConfig(null);
  };

  const handleKiteLogout = () => {
    sessionStorage.removeItem(KITE_SESSION_KEY);
    setKiteSession(null);
  };

  // While exchanging the Kite token show a simple splash
  if (kiteLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#90caf9', fontSize: 16 }}>
        Connecting to Zerodha…
      </div>
    );
  }

  // Kite auth error — show message with option to retry
  if (kiteError) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0f', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, color: '#f44336', padding: 24 }}>
        <div style={{ fontSize: 16 }}>Kite login failed: {kiteError}</div>
        <button style={{ padding: '8px 20px', background: '#387ed1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }} onClick={() => setKiteError(null)}>
          Go back
        </button>
      </div>
    );
  }

  // Active Kite session — only admins reach this
  if (kiteSession && user?.role === 'ADMIN') {
    // If stream started → go to AnalyzerPage wired to Kite endpoints
    if (isAnalyzing && config?.kiteMode) {
      return (
        <div className="App">
          <AnalyzerPage
            config={config}
            onLogout={handleKiteLogout}
            onBack={() => { setIsAnalyzing(false); setConfig(null); }}
            currentUser={user}
            onUpdateUser={handleUpdateUser}
          />
        </div>
      );
    }
    return (
      <KiteDashboard
        kiteSession={kiteSession}
        currentUser={user}
        onLogout={handleKiteLogout}
        onStartAnalysis={(cfg) => { setConfig(cfg); setIsAnalyzing(true); }}
      />
    );
  }

  if (!user) {
    return <AuthPage onAuth={handleAuth} />;
  }

  if (!isAnalyzing) {
    return (
      <LoginPage
        onLogin={handleLogin}
        user={user}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className="App">
      <AnalyzerPage
        config={config}
        onLogout={handleLogout}
        onBack={handleBackToLogin}
        currentUser={user}
        onUpdateUser={handleUpdateUser}
      />
    </div>
  );
}

export default App;
